// Explore data, shared by the render server (server/index.js) and the app's fallback (src/utils/exploreApi.js).
// Reports carry all_labels with every YOLO box (up to 20 KB each). The lists only need the distinct label
// names, so build() reduces each report to a small card once, and part() hands out only what a view needs.
const PAGE = 12;

const labelNames = (r) => [...new Set((Array.isArray(r.all_labels) ? r.all_labels : [])
  .map((l) => l && (l.label || l)).filter((x) => typeof x === 'string' && x))];
const time = (r) => Date.parse(r.created || r.timestamp || 0) || 0;
const cityOf = (r) => (r.city && r.city.trim()) || 'Other';
const countryOf = (r) => (r.country && r.country.trim()) || 'Other';

function card(r) {
  return {
    id: r.id, submission_id: r.submission_id, comment: r.comment || '', severity_level: r.severity_level,
    city: r.city || '', region: r.region || '', country: r.country || '', created: r.created, timestamp: r.timestamp,
    upvotes: Number(r.upvotes) || 0, all_labels: labelNames(r).slice(0, 4).map((label) => ({ label })),
  };
}

function point(r) {
  return {
    id: r.id, submission_id: r.submission_id, geo: r.geo, severity: Number(r.severity_level) || 2,
    title: String(r.comment || '').slice(0, 120), place: [r.city, r.region].map((x) => (x || '').trim()).filter(Boolean).join(', '),
    labels: labelNames(r).slice(0, 3),
  };
}

// Ranked rows for the city and country tabs (count, severity mix, most common label, bar width)
function rows(reports, keyFn) {
  const map = {};
  reports.forEach((r) => {
    const name = keyFn(r);
    const row = map[name] || (map[name] = { name, count: 0, mix: { 1: 0, 2: 0, 3: 0 }, labels: {} });
    row.count += 1;
    const lvl = Number(r.severity_level);
    if (row.mix[lvl] !== undefined) row.mix[lvl] += 1;
    labelNames(r).forEach((l) => { row.labels[l] = (row.labels[l] || 0) + 1; });
  });
  const list = Object.values(map).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const top = list.length ? list[0].count : 1;
  return list.map((row, i) => {
    const best = Object.entries(row.labels).sort((a, b) => b[1] - a[1])[0];
    return { name: row.name, count: row.count, mix: row.mix, rank: i + 1, high: row.mix[3],
      pct: Math.max(4, Math.round((row.count / top) * 100)), topLabel: best ? best[0] : '' };
  });
}

function build(reports) {
  const sorted = [...reports].sort((a, b) => time(b) - time(a));
  const cards = sorted.map(card);
  return {
    at: Date.now(),
    cards,
    byCity: groupIdx(sorted, cityOf),
    byCountry: groupIdx(sorted, countryOf),
    top: [...cards].sort((a, b) => b.upvotes - a.upvotes || time(b) - time(a)).slice(0, 3),
    cities: rows(sorted, cityOf),
    countries: rows(sorted, countryOf),
    points: sorted.filter((r) => r.geo).map(point),
  };
}
function groupIdx(sorted, keyFn) {
  const g = {};
  sorted.forEach((r, i) => { (g[keyFn(r)] = g[keyFn(r)] || []).push(i); });
  return g;
}

// part=summary | recent (page) | city (name) | country (name) | points
function part(d, params = {}) {
  const page = Math.max(1, Number(params.page) || 1);
  const slice = (list) => ({ items: list.slice((page - 1) * PAGE, page * PAGE), page, more: list.length > page * PAGE });
  switch (params.part) {
    case 'recent': return slice(d.cards);
    case 'city': return { ...slice((d.byCity[params.name] || []).map((i) => d.cards[i])), name: params.name };
    case 'country': return { ...slice((d.byCountry[params.name] || []).map((i) => d.cards[i])), name: params.name };
    case 'points': return { items: d.points };
    default: return { total: d.cards.length, top: d.top, recent: slice(d.cards), cities: d.cities, countries: d.countries };
  }
}

// Same filter and columns for server and fallback
const FILTER = "category ~ 'nuisance' && report_public = true";
const FIELDS = 'id,submission_id,geo,comment,severity_level,city,region,country,all_labels,created,timestamp,upvotes';

module.exports = { build, part, FILTER, FIELDS, PAGE };
